import { useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { GlobalContext } from '../contexts/GlobalContext';
import { apiAuth } from '../resources/api/apiAuth';
import { useApiErrorHandler } from './useApiErrorHandler';
import { is2FaTokenData, parseAccessToken } from '../helpers/parseAccessToken';
import { captureException } from '../sentry';
import { useHistory } from 'react-router-dom';
import { GuestRoutes } from '../routers/GuestRouter';
import { UUID } from '../types/uuid';

export enum EAuthResponse {
  Success,
  Unauthorized,
  Unexpected,
  NotImplemented,
  SecondFactorNeeded,
  CodeExpired,
  TokenExpired,
}

export interface ITokenData {
  access_token: string;
  refresh_token: string;
}

const { useLogout, useVerify2fa, useLogin, useSend2fa } = apiAuth;

export const useAuth = () => {
  const handleError = useApiErrorHandler();

  const { setSignInFastTrack } = useContext(GlobalContext);
  const { setIdentity, auth2faCache, setAuth2faCache, forgetCurrentIdentity } = useContext(AuthContext);

  const { execute: signOut } = useLogout();
  const { execute: verify2fa } = useVerify2fa();
  const { execute: login } = useLogin();
  const { execute: send2fa } = useSend2fa();

  const history = useHistory();

  const handleLoginSuccess = useCallback((response: ITokenData) => {
    const parsedToken = parseAccessToken(response.access_token);

    setAuth2faCache(null);
    setSignInFastTrack(null);

    setIdentity({
      email: parsedToken.email,
      tokenData: response,
      userId: parseAccessToken(response.access_token).user_id,
    });
  }, []);

  const twoFactorAuth = useCallback(
    async (deviceId: UUID, code: string): Promise<EAuthResponse> => {
      if (!auth2faCache) {
        return EAuthResponse.Unexpected;
      }
      try {
        const response = await verify2fa(deviceId, code);

        if (response.status_code === 200) {
          handleLoginSuccess(response.payload);
          return EAuthResponse.Success;
        }
      } catch (e) {
        const message: string | undefined = (e as any)?.response?.data?.message || (e as any)?.response?.data?.reason;

        if (message === 'exp claim verification failed: expired') {
          return EAuthResponse.TokenExpired;
        } else if (message === 'Forbidden') {
          return EAuthResponse.Unauthorized;
        }
        handleError(e);
      }
      return EAuthResponse.Unexpected;
    },
    [verify2fa, handleLoginSuccess, auth2faCache]
  );

  const authenticate = useCallback(
    async (email: string, password: string): Promise<EAuthResponse> => {
      try {
        const result = await login(email, password);

        if (result.status_code !== 200) {
          return EAuthResponse.Unauthorized;
        }
        const token = result.payload.access_token;
        const parsedToken = parseAccessToken(token);

        if (is2FaTokenData(parsedToken.data)) {
          setAuth2faCache({
            email: parsedToken.email,
            access_token: token,
            devices: result.payload.mfa_devices ?? [],
            message: result.payload.mfa_message ?? '',
          });
          return EAuthResponse.SecondFactorNeeded;
        }

        if (!result.payload.refresh_token) {
          return EAuthResponse.Unexpected;
        }

        handleLoginSuccess(result.payload);
        return EAuthResponse.Success;
      } catch (e) {
        const statusCode = (e as any)?.response?.data?.status_code;
        if (statusCode >= 400 && statusCode < 500) {
          return EAuthResponse.Unauthorized;
        } else {
          captureException(e);
          return EAuthResponse.Unexpected;
        }
      }
    },
    [login, handleLoginSuccess]
  );

  const logOut: () => Promise<void> = useCallback(async () => {
    try {
      try {
        await signOut();
      } catch (e) {
        console.log(e);
      }
      forgetCurrentIdentity();
      history.push(GuestRoutes.SignIn());
    } catch (e) {
      handleError(e);
    }
  }, []);

  const renewMfa = useCallback(
    async (device_id: UUID) => {
      if (!auth2faCache) {
        return EAuthResponse.Unexpected;
      }
      try {
        await send2fa(device_id);
        return EAuthResponse.Success;
      } catch (e) {
        handleError(e);
        return EAuthResponse.Unexpected;
      }
    },
    [send2fa]
  );

  return {
    authenticate,
    logOut,
    renewMfa,
    twoFactorAuth,
  };
};
